import { useState, type CSSProperties, type ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { PROMO_DISCLOSURE_LABEL } from '@/features/billing/checkout-code-helpers';

/**
 * Collapses a promo-code field behind an inline "Have a promo code?" text link.
 *
 * Every promo surface (`RedeemCodeRow` on `/billing`, `UpgradePromoField` in the
 * upgrade confirm dialog, `CheckoutDialog` before the Paddle overlay) used to
 * paint its input open, which put an empty text box in front of the ~all
 * customers who don't have a code — and, in the two dialogs, an empty field
 * directly above the money the dialog is actually asking them to confirm. The
 * link states the question and costs one click for the minority who answer yes.
 *
 * **One-way by default.** The disclosure itself has no collapse-again affordance: once the field is open it
 * stays open for the life of the mount, including after a code is accepted (the
 * success banner sits under a field the customer may still want to correct).
 * The surfaces that should start over — the checkout dialog between two
 * different plans, the upgrade dialog between two opens — do it by unmounting,
 * which resets this state for free. Nothing here needs to re-close in place.
 *
 * It is deliberately dumb: no knowledge of validation, mutations, or the
 * "already discounted" gate. A blocked surface renders its info banner and
 * never mounts this at all — there is no point offering to open a field the
 * customer isn't allowed to use.
 */
type Props = {
  /**
   * Start expanded. For a surface that can mount with promo state already on it
   * (an applied code, a rejection) — that must never hide behind the link.
   */
  defaultOpen?: boolean;
  /**
   * The promo field. Receives `autoFocus` for its input, true only when the
   * customer opened the disclosure themselves — a `defaultOpen` mount must not
   * yank focus out of the dialog this sits inside.
   *
   * Also receives `close`, which collapses the field back to the link. The
   * disclosure never calls it — see "One-way" above — so a surface only closes
   * if it chooses to render its own Cancel (the billing page does: a field
   * opened by mistake otherwise sits on the plan card for the whole visit).
   */
  children: (state: { autoFocus: boolean; close: () => void }) => ReactNode;
  className?: string;
  style?: CSSProperties;
};

export function PromoCodeDisclosure({
  defaultOpen = false,
  children,
  className,
  style,
}: Props) {
  const [open, setOpen] = useState(defaultOpen);
  // `open` can only flip true through the trigger below, so "open and it didn't
  // start that way" is an exact test for "the customer clicked".
  const autoFocus = open && !defaultOpen;

  if (open) {
    return (
      <div className={className} style={style}>
        {children({ autoFocus, close: () => setOpen(false) })}
      </div>
    );
  }

  return (
    <div className={className} style={style}>
      <Button
        type="button"
        variant="link"
        // Reset the variant's button box back to inline-text metrics — this is
        // a link in a sentence position, not a control with a hit area.
        className="h-auto p-0 text-sm font-medium underline"
        onClick={() => setOpen(true)}
      >
        {PROMO_DISCLOSURE_LABEL}
      </Button>
    </div>
  );
}
